import React, { useMemo, useState } from "react";
import { useSketchDraw } from "@penwell/draw-on";
import styles from "./Home.module.css";
import { ArrowRight, Check, Copy, Download } from "../common/Icons";
import LiquidGlass from "../common/LiquidGlass";
import Reveal from "../common/Reveal";
import { SKETCH_PALETTE } from "../common/sketchPalette";
import useDrawOnceInView from "../../hooks/useDrawOnceInView";
import { buildHeroSketch } from "./heroSketch";

const EMAIL = "pratiksingh067@gmail.com";
const RESUME_URL = "../Pratik_Singh.pdf";

/** Vivus counts in animation frames. */
const SKETCH_DURATION = 240;

const facts = [
  { value: "5 years", label: "shipping to production" },
  { value: "GenAI", label: "portals, streaming, LLM context" },
  { value: "Accenture", label: "senior software engineer, current" },
];

const Home = ({ theme = "light" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // The hero drawing is rendered by @penwell/draw-on, the package published in
  // the open-source section below. It draws on load, because the hero is
  // already in view; threshold 0 keeps that true on small screens too.
  const palette = SKETCH_PALETTE[theme === "dark" ? "dark" : "light"];
  const markup = useMemo(() => buildHeroSketch(palette), [palette]);

  const { containerRef, play, status } = useSketchDraw({
    svgMarkup: markup,
    autoPlay: false,
    duration: SKETCH_DURATION,
    type: "oneByOne",
    revealStart: 0.6,
    stroke: palette.ink,
  });

  const [sectionRef] = useDrawOnceInView(markup, {
    play,
    isError: status === "error",
    threshold: 0,
  });

  return (
    <section className={styles.container} id="top" ref={sectionRef}>
      <div className={styles.content}>
        <Reveal className={styles.eyebrow}>
          <span className={styles.statusDot} aria-hidden="true" />
          <span className={styles.statusText}>Open to senior roles</span>
          <span className={styles.eyebrowRule} aria-hidden="true" />
          <span className={styles.eyebrowMeta}>Bangalore · UTC+5:30</span>
        </Reveal>

        <Reveal as="h1" className={styles.title} delay={70}>
          I build <em className={styles.titleAccent}>AI products</em> and the interfaces they live in.
        </Reveal>

        <Reveal as="p" className={styles.lede} delay={140}>
          Senior full-stack engineer, five years in. Mostly React, Next.js and TypeScript on the
          front with Node behind it, and a growing share of it in LLM tooling, streaming
          pipelines and 3D interfaces.
        </Reveal>

        <Reveal className={styles.actions} delay={210}>
          <a className={styles.primaryBtn} href="#projects">
            <span>See selected work</span>
            <ArrowRight size={16} />
          </a>
          <button
            type="button"
            className={styles.quietBtn}
            onClick={handleCopyEmail}
            title="Copy email address"
          >
            {copied ? <Check size={15} className={styles.copiedIcon} /> : <Copy size={15} />}
            <span>{copied ? "Copied" : "Copy email"}</span>
          </button>
          <a
            className={styles.ghostLink}
            href={RESUME_URL}
            download="Pratik_Singh.pdf"
          >
            <Download size={15} />
            <span>Resume</span>
          </a>
        </Reveal>

        <Reveal as="dl" className={styles.facts} delay={280}>
          {facts.map((fact) => (
            <div key={fact.label} className={styles.fact}>
              <dt className={styles.factValue}>{fact.value}</dt>
              <dd className={styles.factLabel}>{fact.label}</dd>
            </div>
          ))}
        </Reveal>
      </div>

      <Reveal
        as={LiquidGlass}
        blur="full"
        wash
        className={styles.panel}
        delay={180}
        aria-label="The layers I work across, drawn on load"
      >
        <div className={styles.panelHead}>
          <span className={styles.panelLabel}>Fig. 01</span>
          <span className={styles.panelTitle}>The stack</span>
        </div>

        <div className={styles.sketch}>
          <div ref={containerRef} className={styles.sketchHost} />
        </div>

        <div className={styles.panelFoot}>
          <a className={styles.panelLink} href={`mailto:${EMAIL}`}>
            {EMAIL}
          </a>
          <a className={styles.panelNote} href="#drawon">
            Drawn with @penwell/draw-on
          </a>
        </div>
      </Reveal>
    </section>
  );
};

export default Home;